/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-member-access, typescript/no-unsafe-argument, typescript/no-unsafe-call, typescript/no-unsafe-return -- storybook internal runtime; polymorphic story types are any */
import * as React from 'react'
import {
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  use,
} from 'react'
import { SB } from './storybook-ui'
import { convertToStorybookNavTree } from './convert-to-storybook-nav-tree'
import type { NavTree, StorybookNavNode } from './convert-to-storybook-nav-tree'
import { StorybookSidebar } from './storybook-sidebar'
import { StorybookRulesOverlay } from './storybook-rules-overlay'
import { useNanoRouter } from './use-nano-router'
import { useStorybookUrlSettings } from './use-url-settings'
import { BasicErrorBoundary } from '@w/utils-fe'
import { isLazyStory, isLazyWrapper, preloadLazyWrapper, story } from './story'
import type { RootStory, Story, StoryWrapper } from './story'
import type { Toast } from './toaster'
import { useEventListener, usePrevious } from '@w/utils-fe'
import { SimpleStoryControlsEditor } from './props-editor'
import {
  defaultStorybookSettings,
  type StorybookSettings,
} from './storybook-settings'
import type { StoryControlsEditor } from './story-controls'
import type { StoryDocs, StoryDocsRenderer } from './story-docs'
import { getDefaults } from '@w/schema'
import { LoadingIndicator } from '../components/loading-indicator'

export interface StorybookProps {
  stories: RootStory
  controlsEditor?: StoryControlsEditor
  /** Host-owned renderer for opaque story documentation payloads. */
  docsRenderer?: StoryDocsRenderer
  defaultControlledEndpoint?: string
  openReactQueryDevtools?: () => void
  openRouterDevtools?: () => void
  openTelemetryDevtools?: () => void
}

function MobileHeader({
  storyName,
  parentName,
  node,
  parent,
  navTree,
  onNavigate,
}: {
  storyName: string
  parentName?: string
  node?: StorybookNavNode
  parent?: StorybookNavNode
  navTree: NavTree
  onNavigate: (id: string) => void
}) {
  const [hidden, setHidden] = useState(false)
  const grandparent = parent?.parentId
    ? navTree.getNode(parent.parentId)
    : undefined
  const parentOptions = grandparent?.children ?? navTree.navigation
  const storyOptions = parent?.children ?? (node ? [node] : [])

  const navigateToOption = (id: string) => {
    const target = navTree.getNode(id)
    if (!target) return
    onNavigate(
      target.type === 'story' ? target.id : navTree.getFirstStoryId(target.id),
    )
  }

  return (
    <header
      data-testid="storybook-mobile-header"
      className={SB.cn(
        'flex md:hidden shrink-0 items-center gap-2 border-b px-4 overflow-hidden transition-[height,opacity] duration-200 ease-linear',
        hidden ? 'h-0 opacity-0 border-b-0' : 'h-16 opacity-100',
      )}
    >
      <SB.Sidebar.Trigger className="-ml-1" />
      <SB.Breadcrumb.Root className="min-w-0 flex-1">
        <SB.Breadcrumb.List className="flex-nowrap gap-2">
          {parentName && (
            <>
              <SB.Breadcrumb.Item className="min-w-0">
                <MobileBreadcrumbSegment
                  currentId={parent?.id}
                  label={parentName}
                  options={parentOptions}
                  testId="storybook-mobile-component-breadcrumb"
                  onNavigate={navigateToOption}
                />
              </SB.Breadcrumb.Item>
              <SB.Breadcrumb.Separator />
            </>
          )}
          <SB.Breadcrumb.Item className="min-w-0">
            <MobileBreadcrumbSegment
              current
              currentId={node?.id}
              label={storyName}
              options={storyOptions}
              testId="storybook-mobile-story-breadcrumb"
              onNavigate={navigateToOption}
            />
          </SB.Breadcrumb.Item>
        </SB.Breadcrumb.List>
      </SB.Breadcrumb.Root>
      <button
        type="button"
        onClick={() => {
          setHidden(true)
        }}
        className="hover:bg-surface-3 flex size-7 items-center justify-center rounded-md"
        aria-label="Hide header"
      >
        <SB.icons.X className="size-4" />
      </button>
    </header>
  )
}

function MobileBreadcrumbSegment({
  current,
  currentId,
  label,
  options,
  testId,
  onNavigate,
}: {
  current?: boolean
  currentId?: string
  label: string
  options: StorybookNavNode[]
  testId: string
  onNavigate: (id: string) => void
}) {
  const labelClassName = SB.cn(
    'truncate',
    current ? 'text-on-surface' : 'text-on-surface-secondary',
  )

  if (options.length < 2) {
    return current ? (
      <SB.Breadcrumb.Page className={labelClassName}>
        {label}
      </SB.Breadcrumb.Page>
    ) : (
      <span className={labelClassName}>{label}</span>
    )
  }

  return (
    <SB.DropdownMenu.Root>
      <SB.DropdownMenu.Trigger
        render={
          <button
            type="button"
            data-testid={testId}
            className={SB.cn(
              labelClassName,
              'hover:bg-primary/8 data-[popup-open]:bg-primary/8 flex min-w-0 cursor-pointer items-center gap-1 rounded-md px-1.5 py-1 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/60',
            )}
          >
            <span className="truncate">{label}</span>
            <SB.icons.ChevronDown className="size-3.5 shrink-0 opacity-60" />
          </button>
        }
      />
      <SB.DropdownMenu.Content
        align="start"
        className="max-w-[calc(100vw-2rem)] min-w-40"
        sideOffset={8}
      >
        <SB.DropdownMenu.RadioGroup
          value={currentId}
          onValueChange={onNavigate}
        >
          {options.map((option) => (
            <SB.DropdownMenu.RadioItem key={option.id} value={option.id}>
              {option.type === 'story' ? (
                <SB.icons.FileText />
              ) : (
                <SB.icons.Puzzle />
              )}
              {option.name}
            </SB.DropdownMenu.RadioItem>
          ))}
        </SB.DropdownMenu.RadioGroup>
      </SB.DropdownMenu.Content>
    </SB.DropdownMenu.Root>
  )
}

const LAST_STORY_KEY = 'storybook-last-story-id'

export function Storybook(props: StorybookProps) {
  return (
    <Suspense fallback={<StorybookRootFallback />}>
      <StorybookContent {...props} />
    </Suspense>
  )
}

function StorybookContent({
  stories,
  controlsEditor,
  docsRenderer,
  defaultControlledEndpoint,
  openReactQueryDevtools,
  openRouterDevtools,
  openTelemetryDevtools,
}: StorybookProps) {
  const { searchParams, navigate } = useNanoRouter()
  const { toasts, showToast, dismissToast } = useToasts()

  const activeId =
    // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- intentional: empty string falls through to next source
    searchParams.get('id') || localStorage.getItem(LAST_STORY_KEY) || ''
  const navTree = convertToStorybookNavTree(stories.stories)
  const [navbarRerenderNumber, setNavbarRerenderNumber] = useState(0) // quick hack: shadcn sidebar stores copy of the state and when we navigate without it we need to force it to rerender

  const navigateToStory = useCallback(
    (id: string) => {
      const params = new URLSearchParams(location.search)
      params.set('id', id)
      navigate(`?${params.toString()}`)
    },
    [navigate],
  )

  useEffect(() => {
    if (activeId) {
      localStorage.setItem(LAST_STORY_KEY, activeId)
    } else {
      // eslint-disable-next-line rc/set-state-in-effect
      setNavbarRerenderNumber((prev) => prev + 1)
      navigateToStory(navTree.getFirstStoryId())
    }
  }, [activeId, navTree, navigateToStory])

  const [settings, setSettings] = useStorybookUrlSettings()
  const [renderNumber, setRenderNumber] = useState(0)
  const rerenderStory = () => {
    showToast('Story rerendered')
    setRenderNumber(renderNumber + 1)
  }

  const previousNetworkType = usePrevious(settings.fakeNetwork.type)
  useEffect(() => {
    if (
      (previousNetworkType === 'off' && settings.fakeNetwork.type === 'fast') ||
      (previousNetworkType === 'fast' && settings.fakeNetwork.type === 'off')
    ) {
      // eslint-disable-next-line rc/set-state-in-effect
      setRenderNumber((prev) => prev + 1) // radical network change rerender
    }
  }, [previousNetworkType, settings.fakeNetwork.type])

  const toggleTheme = () => {
    setSettings({ ...settings, reversedTheme: !settings.reversedTheme })
  }

  useEventListener('keydown', (e) => {
    if (!e.altKey) return // mac intercepts cmd+alt+d (opens dock)
    // e.key might be Dead. A dead key is a key that does not generate a character by itself but modifies the character generated by the key pressed immediately after.
    let prevented = true
    if (e.code === 'KeyT') toggleTheme()
    else if (e.code === 'KeyR') rerenderStory()
    else if (e.code === 'KeyL') {
      setSettings((old) => ({
        ...old,
        language: old.language === 'en' ? 'ru' : 'en',
      }))
    } else if (e.code === 'KeyS') {
      openTelemetryDevtools?.()
    } else prevented = false

    if (prevented) e.preventDefault()
  })

  const renderedActiveId = useDeferredValue(activeId)
  const storyPending = renderedActiveId !== activeId
  const selectedStoryReference = navTree.getStory(renderedActiveId)
  const selectedStory = isLazyStory(selectedStoryReference)
    ? use(selectedStoryReference.load())
    : selectedStoryReference
  const activeStory =
    selectedStory ??
    story(() => {
      return (
        <div className="grid h-dvh place-items-center">
          <div className="flex flex-col gap-2">
            <p className="text-center">Story not found</p>
            <SB.Button
              variant="filled"
              onClick={() => {
                navigateToStory(navTree.getFirstStoryId())
              }}
            >
              Go to 1st Story
            </SB.Button>
          </div>
        </div>
      )
    })
  const { node, parent } = navTree.getNodeAndParent(renderedActiveId) ?? {}
  const { node: activeNode, parent: activeParent } =
    navTree.getNodeAndParent(activeId) ?? {}
  const storyName = node?.name ?? ''
  const parentName = parent?.name

  const isRight = !!activeStory.params?.rightSidebar
  const storyPaneRef = useRef<HTMLElement>(null)
  const navigateFromSidebar = (id: string) => {
    navigateToStory(id)
    storyPaneRef.current?.focus()
  }
  const sidebar = (
    <StorybookSidebar
      key={navbarRerenderNumber}
      navTree={navTree}
      onStoryClick={(id) => {
        navigateFromSidebar(id)
      }}
      onNodeClick={(id) => {
        navigateFromSidebar(navTree.getFirstStoryId(id))
      }}
      settings={{
        ...settings,
        setFakeNetwork: (cb) => {
          setSettings((old) => ({ ...old, fakeNetwork: cb(old.fakeNetwork) }))
        },
        setLanguage: (language) => {
          setSettings((old) => ({ ...old, language }))
        },
        setMinimal: (minimal) => {
          setSettings((old) => ({ ...old, minimal }))
        },
        setSourceColor: (sourceColor) => {
          setSettings((old) => ({ ...old, sourceColor }))
        },
        setRules: (cb) => {
          setSettings((old) => ({ ...old, rules: cb(old.rules) }))
        },
        toggleTheme, // () => setSettings({ ...settings, theme: settings.theme === 'light' ? 'dark' : 'light' }),
        rerenderStory,
      }}
      toasts={toasts}
      onToastDismiss={dismissToast}
      activeId={activeId}
      pendingId={storyPending ? activeId : undefined}
      rightSidebar={activeStory.params?.rightSidebar}
      defaultControlledEndpoint={defaultControlledEndpoint}
      openReactQueryDevtools={openReactQueryDevtools}
      openRouterDevtools={openRouterDevtools}
      openTelemetryDevtools={openTelemetryDevtools}
    />
  )

  return (
    <SB.Sidebar.Provider
      revealOnHover
      open={settings.sidebarPinned}
      onOpenChange={(sidebarPinned) => {
        setSettings((old) => ({ ...old, sidebarPinned }))
      }}
    >
      {!isRight && sidebar}
      <SB.Sidebar.Inset
        ref={storyPaneRef}
        tabIndex={-1}
        key={`${renderedActiveId}-${renderNumber}`}
        className="flex h-svh flex-col"
      >
        <MobileHeader
          storyName={activeNode?.name ?? storyName}
          parentName={activeParent?.name ?? parentName}
          node={activeNode}
          parent={activeParent}
          navTree={navTree}
          onNavigate={navigateToStory}
        />
        <div
          className="relative flex-1 overflow-y-auto"
          aria-busy={storyPending}
        >
          <BasicErrorBoundary>
            <Suspense fallback={<StoryPaneLoading storyName={storyName} />}>
              <StoryRenderer
                prepareEnabled={selectedStory !== undefined}
                preparationId={`${renderedActiveId}:${renderNumber}`}
                settings={settings}
                root={stories}
                wrappers={node?.wrappers ?? []}
                Story={activeStory}
                componentName={parentName ?? storyName}
                layout={activeStory.params?.layout ?? 'fullscreen'}
                showToast={showToast}
                controlsEditor={controlsEditor}
                docsRenderer={docsRenderer}
              />
            </Suspense>
          </BasicErrorBoundary>
        </div>
      </SB.Sidebar.Inset>
      {isRight && sidebar}
      <StorybookRulesOverlay
        rules={settings.rules}
        onRulesChange={(rules, options) => {
          setSettings((old) => ({ ...old, rules }), options)
        }}
      />
    </SB.Sidebar.Provider>
  )
}

type StoryRendererProps = Base

function StoryPaneLoading({ storyName }: { storyName?: string }) {
  const displayName = storyName ?? 'story'
  return (
    <div
      className="grid min-h-full w-full place-items-center p-6"
      role="status"
      aria-live="polite"
    >
      <div className="text-on-surface-secondary flex flex-col items-center gap-4 text-center text-sm">
        <LoadingIndicator className="size-16" aria-hidden="true" />
        <span>Preparing {displayName}…</span>
      </div>
    </div>
  )
}

function StorybookRootFallback() {
  return (
    <div
      className="min-h-svh w-full bg-black text-white"
      role="status"
      aria-live="polite"
    >
      <div className="h-px w-full overflow-hidden bg-white/10">
        <div className="h-full w-1/4 animate-pulse bg-white/60" />
      </div>
      <span className="sr-only">Loading Storybook…</span>
    </div>
  )
}

function StoryRenderer(props: StoryRendererProps) {
  const { Story, root, wrappers, settings, preparationId, prepareEnabled } =
    props
  const requiresPreparation =
    prepareEnabled &&
    Boolean(
      root.prepare ??
      wrappers.find((wrapper) => wrapper.prepare)?.prepare ??
      Story.prepare ??
      Story.params?.router ??
      [root, ...wrappers].some((scope) => isLazyWrapper(scope.wrapper)),
    )
  const [ready, setReady] = useState(!requiresPreparation)
  const [error, setError] = useState<Error | undefined>()
  // oxlint-disable-next-line typescript/no-explicit-any -- prepare result can be any
  const [prepareResult, setPrepareResult] = useState<any>()
  const [routerCtx, setRouterCtx] = useState<unknown>()
  const [preparationSettings] = useState(() => settings)

  useEffect(() => {
    if (!prepareEnabled) return () => {}
    let subscribed = true
    const session = acquirePreparationSession(
      root,
      preparationId,
      wrappers,
      Story,
      preparationSettings,
    )

    const prepare = async () => {
      try {
        const result = await session.promise
        if (!subscribed || session.controller.signal.aborted) return
        setReady(true)
        setPrepareResult(result.prepareResult)
        setRouterCtx(result.routerCtx)
      } catch (e) {
        if (!subscribed || session.controller.signal.aborted) return
        setError(e as Error)
      }
    }
    void prepare()

    return () => {
      subscribed = false
      session.subscribers -= 1
      session.abortTimer = setTimeout(() => {
        if (session.subscribers === 0) {
          session.controller.abort()
          removePreparationSession(root, preparationId, session)
        }
      }, 0)
    }
  }, [
    Story,
    preparationId,
    prepareEnabled,
    preparationSettings,
    root,
    wrappers,
  ])

  if (error) throw error
  if (!ready) return <StoryPaneLoading storyName={props.componentName} />

  return (
    <InitializedStoryRenderer
      {...props}
      prepareResult={prepareResult}
      routerCtx={routerCtx}
    />
  )
}

interface StoryPreparationResult {
  prepareResult: unknown
  routerCtx: unknown
}

interface PreparationSession {
  abortTimer?: ReturnType<typeof setTimeout>
  controller: AbortController
  promise: Promise<StoryPreparationResult>
  subscribers: number
}

const preparationSessions = new WeakMap<
  RootStory,
  Map<string, PreparationSession>
>()

function acquirePreparationSession(
  root: RootStory,
  preparationId: string,
  wrappers: StoryWrapper[],
  Story: Story,
  settings: StorybookSettings,
): PreparationSession {
  const sessions = preparationSessions.get(root) ?? new Map()
  preparationSessions.set(root, sessions)
  const pending = sessions.get(preparationId)
  if (pending && !pending.controller.signal.aborted) {
    clearTimeout(pending.abortTimer)
    pending.subscribers += 1
    return pending
  }

  const controller = new AbortController()
  const session: PreparationSession = {
    controller,
    promise: undefined as unknown as Promise<StoryPreparationResult>,
    subscribers: 1,
  }
  sessions.set(preparationId, session)
  session.promise = Promise.resolve().then(() =>
    runStoryPreparation(root, wrappers, Story, {
      signal: controller.signal,
      settings,
    }),
  )
  // A settled preparation still owns the mounted tree's context. StrictMode
  // effect replay must reacquire that same owner until its last subscriber leaves.
  void session.promise.catch(() => {})
  return session
}

function removePreparationSession(
  root: RootStory,
  preparationId: string,
  session: PreparationSession,
) {
  const sessions = preparationSessions.get(root)
  if (sessions?.get(preparationId) !== session) return
  sessions.delete(preparationId)
  if (sessions.size === 0) preparationSessions.delete(root)
}

export async function runStoryPrepares(
  root: RootStory,
  wrappers: StoryWrapper[],
  Story: Story,
  context?: Parameters<NonNullable<Story['prepare']>>[0],
) {
  const result = await runStoryPreparation(root, wrappers, Story, context)
  return result.prepareResult
}

async function runStoryPreparation(
  root: RootStory,
  wrappers: StoryWrapper[],
  Story: Story,
  context?: Parameters<NonNullable<Story['prepare']>>[0],
): Promise<StoryPreparationResult> {
  const prepareContext = context ?? {
    signal: new AbortController().signal,
    settings: defaultStorybookSettings(),
  }
  await Promise.all(
    [root, ...wrappers].map((scope) => preloadLazyWrapper(scope.wrapper)),
  )
  prepareContext.signal.throwIfAborted()
  if (root.prepare) await root.prepare(prepareContext)
  prepareContext.signal.throwIfAborted()
  for (const wrapper of wrappers) {
    if (wrapper.prepare) await wrapper.prepare(prepareContext)
    prepareContext.signal.throwIfAborted()
  }
  let routerCtx: unknown
  if (Story.params?.router) {
    routerCtx = await Story.params.router.createCtx(prepareContext)
    prepareContext.signal.throwIfAborted()
  }
  let prepareResult: unknown
  if (Story.prepare) {
    const leafContext = Story.params?.router
      ? { ...prepareContext, ctx: routerCtx }
      : prepareContext
    prepareResult = await (
      Story.prepare as (context: typeof leafContext) => unknown
    )(leafContext)
    prepareContext.signal.throwIfAborted()
  }
  return { prepareResult, routerCtx }
}

interface Base {
  prepareEnabled: boolean
  preparationId: string
  root: RootStory
  wrappers: StoryWrapper[]
  Story: Story
  componentName: string
  settings: StorybookSettings
  layout: 'fullscreen' | 'centered'
  showToast: (message: string) => void
  controlsEditor?: StoryControlsEditor
  docsRenderer?: StoryDocsRenderer
}

interface InitializedStoryRendererProps extends Base {
  // oxlint-disable-next-line typescript/no-explicit-any -- prepare result can be any
  prepareResult: any
  routerCtx: unknown
}

function InitializedStoryRenderer({
  root,
  wrappers,
  Story,
  componentName,
  prepareResult,
  settings,
  layout,
  showToast,
  controlsEditor,
  docsRenderer: DocsRenderer,
  routerCtx,
}: InitializedStoryRendererProps) {
  const descriptor = useMemo(() => Story.schema?.describe(), [Story.schema])
  const initialSource = useMemo(
    () => resolveStorySource(Story, prepareResult),
    [Story, prepareResult],
  )

  const [source, setSource] = useState(initialSource)
  const props = resolveStoryPropsFromSource(Story, source)
  const params = resolveStoryParams(Story.params, routerCtx)
  const docs = Story.params?.docs
  // A doc page always gets its own scrollable layout: the live preview stays
  // prominent up top (still respecting the requested centered/fullscreen
  // preview treatment) with prose below, rather than centering the whole
  // page like a plain `centered` story would.
  const Layout = layout === 'fullscreen' ? Fullscreen : Centered

  const canvas = composeStorySubtreeWrappers(
    wrappers,
    settings,
    params,
    <Layout>
      <Story {...props} />
    </Layout>,
  )

  if (docs && !DocsRenderer) {
    throw new Error('Story defines docs but Storybook has no docsRenderer')
  }
  const page =
    docs && DocsRenderer ? (
      <DocPage docs={docs} DocsRenderer={DocsRenderer} preview={canvas} />
    ) : (
      canvas
    )

  const Editor = controlsEditor ?? SimpleStoryControlsEditor
  const schemaValues = descriptor
    ? requirePropsObject(source, 'schema')
    : undefined
  const initialSchemaValues = descriptor
    ? requirePropsObject(initialSource, 'schema')
    : undefined
  const workspace = descriptor ? (
    <Editor
      componentName={componentName}
      descriptor={descriptor}
      values={schemaValues ?? {}}
      initialValues={initialSchemaValues ?? {}}
      onChange={setSource}
      onReset={() => {
        setSource(initialSource)
      }}
      showToast={showToast}
    >
      {page}
    </Editor>
  ) : (
    page
  )

  return composeRootStoryWrapper(root, settings, params, workspace)
}

function resolveStoryParams(
  params: Story['params'],
  routerCtx: unknown,
): Story['params'] {
  if (!params?.router) return params
  return {
    ...params,
    router: {
      ...params.router,
      ctx: routerCtx,
    },
  }
}

/** Resolves automatic story props or the source passed through `getProps`. */
export function resolveStoryProps(
  Story: Story,
  prepareResult?: unknown,
): Record<string, unknown> {
  return resolveStoryPropsFromSource(
    Story,
    resolveStorySource(Story, prepareResult),
  )
}

function resolveStorySource(Story: Story, prepareResult: unknown): unknown {
  const schemaProps = Story.schema ? getDefaults(Story.schema) : undefined
  const hasPreparedData = Story.prepare !== undefined

  let source: unknown
  if (schemaProps && hasPreparedData) {
    source = {
      ...schemaProps,
      ...requirePropsObject(prepareResult, 'prepare'),
    }
  } else if (schemaProps) {
    source = schemaProps
  } else if (hasPreparedData) {
    source = prepareResult
  }

  return source
}

function resolveStoryPropsFromSource(
  Story: Story,
  source: unknown,
): Record<string, unknown> {
  if (Story.getProps) {
    const resolved =
      source === undefined
        ? (Story.getProps as () => unknown)()
        : Story.getProps(source)
    return requirePropsObject(resolved, 'getProps')
  }

  if (source === undefined) return {}
  return requirePropsObject(source, Story.schema ? 'schema' : 'prepare')
}

function requirePropsObject(
  value: unknown,
  source: 'getProps' | 'prepare' | 'schema',
): Record<string, unknown> {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  throw new Error(
    `${source} must produce an object when getProps is not provided or schema props are merged`,
  )
}

export function composeStoryWrappers(
  root: RootStory,
  wrappers: StoryWrapper[],
  settings: StorybookSettings,
  params: Story['params'],
  content: React.ReactNode,
) {
  return composeStoryScopes([root, ...wrappers], settings, params, content)
}

function composeRootStoryWrapper(
  root: RootStory,
  settings: StorybookSettings,
  params: Story['params'],
  content: React.ReactNode,
) {
  return composeStoryScopes([root], settings, params, content)
}

function composeStorySubtreeWrappers(
  wrappers: StoryWrapper[],
  settings: StorybookSettings,
  params: Story['params'],
  content: React.ReactNode,
) {
  return composeStoryScopes(wrappers, settings, params, content)
}

function composeStoryScopes(
  scopes: Array<RootStory | StoryWrapper>,
  settings: StorybookSettings,
  params: Story['params'],
  content: React.ReactNode,
) {
  function wrap(index: number): React.ReactNode {
    const scope = scopes[index]
    if (!scope) return content
    const Wrapper = scope.wrapper
    return (
      <Wrapper settings={settings} params={params}>
        {wrap(index + 1)}
      </Wrapper>
    )
  }

  return wrap(0)
}

function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  const showToast = (message: string, type: Toast['type'] = 'default') => {
    const id = Math.random().toString(36).slice(2, 9)
    const newToast: Toast = { id, message, type }
    setToasts((prev) => [...prev, newToast])

    // Auto dismiss after 3 seconds
    setTimeout(() => {
      dismissToast(id)
    }, 3000)
  }

  return { toasts, showToast, dismissToast }
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full w-full">
      {/* Reqs: Keep width 100% for `w-full` work in stories while still centering narrow stories without cutting vertical overflow. */}
      <div className="mx-auto my-auto flex w-full max-w-full justify-center">
        {children}
      </div>
    </div>
  )
}

function Fullscreen({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>
}

/**
 * Shadcn-style doc page: live preview + prose.
 *
 * Split mode gives docs half the available width, constrained to 400–650px.
 * Preview takes the remaining space and keeps its own scroll context while
 * long docs scroll independently.
 *
 * Below 800px of actual container width, a 50% docs pane would fall below its
 * 400px minimum. The layout becomes Preview / Docs tabs instead. Container
 * width—not viewport width—accounts for the Storybook sidebar.
 */
function DocPage({
  docs,
  DocsRenderer,
  preview,
}: {
  docs: StoryDocs
  DocsRenderer: StoryDocsRenderer
  preview: React.ReactNode
}) {
  const [tab, setTab] = useState<'preview' | 'docs'>('preview')

  return (
    <div className="@container h-full w-full" data-slot="storybook-doc-page">
      <div className="flex h-full w-full flex-col @min-[800px]:flex-row">
        <div className="flex shrink-0 border-b @min-[800px]:hidden">
          <DocTab
            active={tab === 'preview'}
            onClick={() => {
              setTab('preview')
            }}
          >
            Preview
          </DocTab>
          <DocTab
            active={tab === 'docs'}
            onClick={() => {
              setTab('docs')
            }}
          >
            Docs
          </DocTab>
        </div>

        <div
          className={SB.cn(
            'min-h-0 min-w-0 flex-1 overflow-y-auto @min-[800px]:w-1/2 @min-[800px]:min-w-[400px] @min-[800px]:max-w-[650px] @min-[800px]:flex-none',
            tab === 'preview' && 'hidden @min-[800px]:block',
          )}
          data-slot="storybook-docs-panel"
        >
          <div className="mx-auto w-full max-w-[650px] min-w-0 px-6 py-10">
            <DocsRenderer docs={docs} />
          </div>
        </div>

        <div
          className={SB.cn(
            'border-border min-h-0 min-w-0 flex-1 overflow-y-auto border-t @min-[800px]:border-t-0 @min-[800px]:border-l',
            tab === 'docs' && 'hidden @min-[800px]:block',
          )}
          data-slot="storybook-preview-panel"
        >
          {preview}
        </div>
      </div>
    </div>
  )
}

function DocTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      className={SB.cn(
        'flex-1 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
        active
          ? 'border-primary text-on-surface'
          : 'text-on-surface-secondary border-transparent',
      )}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  )
}
